// class-info.ts — client-side port of the "Main Class Information" + "Vitality" list-groups that
// options/tables/table_item_class_inc.php renders in PHP (the `if item_type == 'class'` block, ~132-321).
// Reads the same cached class blob: essence/mechanic/caster-type/spellcasting refs resolve to names via
// the names map (catalog + static registries) and to art via iconMap. Markup mirrors the PHP list-group
// rows so the converted block is visually identical.

import { nameOf, mergeNamesWithCatalog, uuidSlot, type ReferencedNames } from '../list-display/helpers';
import { layerEntriesOf, layerOf, itemsCol } from '../shared/array-types';
import { classBlobOf, type ClassBlob } from './blob';

function entryUuids(blob: ClassBlob, layer: string): string[] {
    const out: string[] = [];
    for (const entry of layerEntriesOf(blob, layer)) {
        const id = uuidSlot(entry) || (typeof entry === 'string' ? entry : '');
        if (id) out.push(id);
    }
    return out;
}

function li(html: string): string {
    return '<li class="list-group-item">' + html + '</li>';
}

/** Build the two class-info list-groups from the class blob. Returns '' if the blob is missing. */
export function renderClassInfoHtml(value: unknown): string {
    const classBlob = classBlobOf(value);
    if (!classBlob) return '';
    const names: ReferencedNames = mergeNamesWithCatalog({}, classBlob);
    const link = (id: string, label: string): string =>
        '<span class="link-primary" role="button" onclick="openItemInfo(\'' + escapeHtml(id) + '\');">' + label + '</span>';

    // ── Main Class Information ──────────────────────────────────────────────
    const main: string[] = [];
    main.push('<li class="list-group-item list-group-item-primary border" aria-current="true">'
        + iconMap('class_information') + ' <span class="fw-bold">Main Class Information:</span></li>');

    const primary = entryUuids(classBlob, 'primary_essence');
    if (primary.length) {
        main.push(li('<span class="fw-bold">Primary ' + (primary.length > 1 ? 'Essences' : 'Essence') + ': </span>'
            + primary.map(id => iconMap(id)).join(' ')));
    }

    const mechanic = entryUuids(classBlob, 'class_mechanic')[0];
    if (mechanic) {
        main.push(li('<span class="fw-bold">Class Mechanic: </span>'
            + link(mechanic, iconMap(mechanic) + ' ' + escapeHtml(nameOf(names, mechanic)))));
    }

    const casterEntry = layerEntriesOf(classBlob, 'class_caster_type')[0];
    const casterType = casterEntry ? (uuidSlot(casterEntry) || (typeof casterEntry === 'string' ? casterEntry : '')) : '';
    if (casterType) {
        // Caster types live in the CasterType static registry (NOT the DB items table or the icon
        // catalog), so nameOf()/the UUID lookup can't resolve them — but the layer entry carries the
        // name inline (items.item_name, e.g. "Half Caster" / "No Bending Powers"). Read that first.
        const casterName = itemsCol<string>(casterEntry, 'item_name') || nameOf(names, casterType);
        main.push(li('<span class="fw-bold">Class Caster Type: </span>' + link(casterType, escapeHtml(casterName))));
    }

    const spellEssence = entryUuids(classBlob, 'spellcasting_essence');
    if (spellEssence.length) {
        main.push(li('<span class="fw-bold">Spellcasting Essence: </span>'
            + spellEssence.map(id => '<span role="button" onclick="openItemInfo(\'' + escapeHtml(id) + '\');">' + iconMap(id) + '</span>').join(' ')));
    }

    const spheres = entryUuids(classBlob, 'class_spellcasting_restricted_spell_sphere');
    if (spheres.length) {
        main.push(li('<span class="fw-bold">Restricted Spell Spheres: </span>'
            + spheres.map(id => '<span role="button" onclick="openItemInfo(\'' + escapeHtml(id) + '\');">' + iconMap(id) + '</span>').join(' ')));
    }

    const circles = entryUuids(classBlob, 'class_spellcasting_restricted_spell_circle');
    if (circles.length) {
        main.push(li('<span class="fw-bold">Restricted Spell Circles: </span>'
            + circles.map(id => '<span role="button" onclick="openItemInfo(\'' + escapeHtml(id) + '\');">' + iconMap(id) + '</span>').join(' ')));
    }

    const subclassLevel = layerOf(classBlob, 'subclass_gained_at_class_level');
    if (subclassLevel !== undefined && subclassLevel !== null && String(subclassLevel) !== '') {
        main.push(li('<span class="fw-bold">Subclass Gained at Class Level: </span>'
            + '<span class="badge bg-dark border border-light">' + escapeHtml(String(subclassLevel)) + '</span>'));
    }

    // ── Vitality ────────────────────────────────────────────────────────────
    const vFirst = layerOf(classBlob, 'class_v_at_first_level');
    const vDie = layerOf(classBlob, 'class_v_die');
    const vitality = '<p class="card-text"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-danger border" aria-current="true">' + iconMap('vitality') + ' <span class="fw-bold">Vitality:</span></li>'
        + li('<span class="fw-bold">Vitality at 1st Level:</span> <span class="badge bg-danger border">' + escapeHtml(String(vFirst ?? '')) + '</span>')
        + li('<span class="fw-bold">Vitality at Higher Levels:</span> <span class="badge bg-primary border">' + escapeHtml(String(vDie ?? '')) + '</span>')
        + li('<span class="fw-bold">Vitality From ' + iconMap('body') + ':</span> ROUNDDOWN(Body * (Total Level / 2))')
        + '</ul></p>';

    return '<p class="card-text"><ul class="list-group border">' + main.join('') + '</ul></p>' + vitality;
}
